import { Thermometer } from 'lucide-react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { celsius, useEnvTemp, useEnvTempSeries } from '@/lib/envTemp'

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
    </div>
  )
}

export function RackTempCard() {
  const { data, isLoading } = useEnvTemp()
  const { data: series } = useEnvTempSeries('24h')

  if (isLoading) return <Skeleton className="h-64" />
  const sensor = data?.sensors[0]
  // No sensor reporting (agent not installed / stale > 10 min) → hide the tile.
  if (!sensor) return null

  const chart = (series?.points ?? [])
    .filter((p) => p.inside_c != null || p.outside_c != null)
    .map((p) => ({
      t: p.b,
      inside: p.inside_c,
      outside: p.outside_c,
      delta:
        p.inside_c != null && p.outside_c != null
          ? Math.round((p.inside_c - p.outside_c) * 10) / 10
          : null,
    }))

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Thermometer className="h-4 w-4 text-primary" />
          Rack temperature — {sensor.sensor_id}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <Stat label="Inside" value={celsius(sensor.inside?.temp_c)} />
          <Stat label="Outside" value={celsius(sensor.outside?.temp_c)} />
          <Stat label="Delta" value={celsius(sensor.delta_c)} />
        </div>

        {chart.length > 1 && (
          <div style={{ width: '100%', height: 180 }}>
            <ResponsiveContainer>
              <LineChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="t"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(v) =>
                    typeof v === 'string' ? new Date(v).toLocaleTimeString() : String(v)
                  }
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(v) => `${v} °C`}
                  stroke="hsl(var(--muted-foreground))"
                  width={60}
                />
                <Tooltip
                  contentStyle={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelFormatter={(v) =>
                    typeof v === 'string' ? new Date(v).toLocaleString() : String(v)
                  }
                  formatter={(value) => celsius(typeof value === 'number' ? value : null)}
                />
                <Line type="monotone" dataKey="inside" name="Inside" stroke="hsl(var(--destructive))" dot={false} strokeWidth={2} connectNulls />
                <Line type="monotone" dataKey="outside" name="Outside" stroke="hsl(var(--primary))" dot={false} strokeWidth={2} connectNulls />
                <Line type="monotone" dataKey="delta" name="Delta" stroke="hsl(var(--muted-foreground))" dot={false} strokeWidth={1} strokeDasharray="4 4" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
